import React, { useState, useEffect } from 'react';
import { Instagram, Facebook } from 'lucide-react';
import NewsletterModal from './NewsletterModal';
import { contentAPI } from '../utils/api';

const TikTokIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Module-level cache — footer + restaurant info never refetch within a session
// so the footer doesn't flash/repaint across page navigations.
let CACHED_FOOTER = null;
let CACHED_INFO = null;
let CACHED_FOOTER_PROMISE = null;
let CACHED_INFO_PROMISE = null;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [footer, setFooter] = useState(CACHED_FOOTER);
  const [info, setInfo] = useState(CACHED_INFO || {});

  useEffect(() => {
    if (!CACHED_FOOTER) {
      if (!CACHED_FOOTER_PROMISE) CACHED_FOOTER_PROMISE = contentAPI.getFooter();
      CACHED_FOOTER_PROMISE.then((d) => {
        CACHED_FOOTER = d || {};
        setFooter(CACHED_FOOTER);
      });
    }
    if (!CACHED_INFO) {
      if (!CACHED_INFO_PROMISE) CACHED_INFO_PROMISE = contentAPI.getRestaurantInfo();
      CACHED_INFO_PROMISE.then((d) => {
        CACHED_INFO = d || {};
        setInfo(CACHED_INFO);
      });
    }
  }, []);

  if (!footer) return null;

  const renderColumn = (col, idx) => {
    if (col.type === 'hours') {
      return (
        <div key={idx}>
          <h4 className="text-lg font-semibold mb-4">{col.title}</h4>
          <div className="space-y-2 text-gray-400">
            <p>Wednesday - Sunday</p>
            <p className="text-white">{info.hours_weekdays || ''}</p>
            <p className="mt-3 text-gray-500 text-sm">Closed Monday &amp; Tuesday</p>
          </div>
        </div>
      );
    }
    if (col.type === 'contact') {
      return (
        <div key={idx}>
          <h4 className="text-lg font-semibold mb-4">{col.title}</h4>
          <div className="space-y-2 text-gray-400">
            <p>{info.address}</p>
            <p className="text-white">{info.phone}</p>
            <p className="text-white">{info.email}</p>
          </div>
        </div>
      );
    }
    if (col.type === 'social') {
      const social = info.social_links || {};
      return (
        <div key={idx}>
          <h4 className="text-lg font-semibold mb-4">{col.title}</h4>
          <div className="flex gap-4">
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                 className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Instagram size={20} />
              </a>
            )}
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                 className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Facebook size={20} />
              </a>
            )}
            {social.tiktok && (
              <a href={social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok"
                 className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <TikTokIcon />
              </a>
            )}
          </div>
          {footer.show_newsletter_button && (
            <div className="mt-6">
              <button
                onClick={() => setShowNewsletter(true)}
                className="inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-md font-semibold text-sm tracking-wide hover:bg-gray-200 transition-colors"
                data-testid="footer-newsletter-btn"
              >
                {footer.newsletter_button_label || 'Join Our Newsletter'}
              </button>
            </div>
          )}
        </div>
      );
    }
    // 'links' default
    return (
      <div key={idx}>
        <h4 className="text-lg font-semibold mb-4">{col.title}</h4>
        <div className="space-y-2 text-gray-400">
          {(col.links || []).map((link, i) => (
            <a key={i} href={link.href} className="block hover:text-white transition-colors">{link.label}</a>
          ))}
        </div>
      </div>
    );
  };

  const cols = footer.columns || [];
  const gridCols = cols.length === 4 ? 'md:grid-cols-4' : cols.length === 3 ? 'md:grid-cols-4' : 'md:grid-cols-3';

  return (
    <footer className="bg-[#0f172a] text-white py-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`grid ${gridCols} gap-12 mb-12`}>
          {/* Brand */}
          <div>
            <div className="mb-4">
              {footer.logo_url && (
                <img src={footer.logo_url} alt={footer.logo_alt || 'Brick Lane'} className="h-12 w-auto object-contain" />
              )}
            </div>
            {footer.tagline && <p className="text-gray-400 leading-relaxed">{footer.tagline}</p>}
          </div>
          {cols.map((col, idx) => renderColumn(col, idx))}
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            {(footer.copyright_text || '© {YEAR} Brick Lane. All rights reserved.').replace('{YEAR}', currentYear)}
          </p>
          <div className="flex gap-6 text-sm text-gray-400">
            {(footer.bottom_links || []).map((link, idx) => (
              <a key={idx} href={link.href} className="hover:text-white transition-colors">{link.label}</a>
            ))}
          </div>
        </div>
      </div>

      <NewsletterModal isOpen={showNewsletter} onClose={() => setShowNewsletter(false)} />
    </footer>
  );
};

export default Footer;
